import React, { useEffect, useState } from 'react'
import AdminLayout from '../../../layouts/AdminLayout'
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner'
import { useGetPermissions } from '../hooks/useGetPermissions'
import { toast } from 'sonner'
import PermissionsTable from '../components/PermissionsTable'
import PaginationComponent from '../components/PaginationComponent'
import SearchComponent from '../components/SearchComponent'
import FullPageError from '../../../shared/components/errors/FullPageError'
import Swal from 'sweetalert2'
import CreatePermissionDialog from '../components/CreatePermissionDialog'
import UpdatePermissionDialog from '../components/UpdatePermissionDialog'
import { z } from 'zod'
import { useDeletePermission } from '../hooks/useDeletePermission'
import { useQueryClient } from '@tanstack/react-query'
import RequireAuth from '../../../auth/middleware/RequireAuth'

const validationIdDelete = z.object({
    id: z.number().int().positive(),
});

const PermissionsPage: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [showCreate, setShowCreate] = useState<boolean>(false);
    const [showUpdate, setShowUpdate] = useState<boolean>(false);
    const [idWillUpdate, setIdWillUpdate] = useState<number>();

    const { data: permissions, isSuccess, isError, isLoading, error } = useGetPermissions({
        name: 'permissions',
        page: currentPage,
        limit: perPage,
        search: searchQuery,
    });

    useEffect(() => {
        if (isError) {
            toast.error(error?.message, {
                id: 'fetch-permissions',
            });
        }
    }, [isError, error]);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);  // Update global searchQuery state
    }

    const handleOpenUpdate = (id: number) => {
        setIdWillUpdate(id);
        setShowUpdate(true);
    }

    const handleCloseUpdate = () => {
        setIdWillUpdate(undefined);
        setShowUpdate(false);
    };

    const { mutate: deletePermission } = useDeletePermission();

    const queryClient = useQueryClient();

    const handleDelete = (permissionId: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to recover this permission!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading('Deleting permission...', {
                    id: 'delete-permission',
                });

                const validation = validationIdDelete.safeParse({
                    id: permissionId
                });

                if (!validation.success) {
                    toast.error('Invalid permission id', {
                        id: 'delete-permission',
                    });
                    return;
                }

                deletePermission(validation.data, {
                    onSuccess: async () => {
                        toast.success('Permission has been deleted', {
                            id: 'delete-permission',
                        });

                        await queryClient.refetchQueries({
                            predicate: (query) => query.queryKey.some((key) => ['permissions'].includes(key as string)),
                        });
                    },
                    onError: () => {
                        toast.error('Failed to delete permission', {
                            id: 'delete-permission',
                        });
                    }
                });
            }
        })
    };

    return (
        <AdminLayout>
            {isError && <FullPageError />}
            {isLoading && <FullPageSpinner />}
            {isSuccess && (
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <div className="row g-3 align-items-center mb-4">
                            <div className="col-12 col-md-auto">
                                <select
                                    className="form-select form-control"
                                    value={perPage}
                                    onChange={(e) => setPerPage(Number(e.target.value))}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="col-12 col-md">
                                <SearchComponent queryKey={searchQuery} onSearch={handleSearch} />
                            </div>
                            <div className="col-12 col-md-auto">
                                {/* Create button */}
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['permission-create']} asComponent>
                                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create</button>
                                </RequireAuth>
                            </div>
                        </div>

                        <div className='table-responsive'>
                            {/* Table */}
                            <PermissionsTable permissions={permissions} onUpdate={handleOpenUpdate} onDelete={handleDelete} />
                        </div>

                        {/* Pagination */}
                        <PaginationComponent
                            totalPages={permissions.pagination.last_page}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            )}

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['permission-create']} asComponent>
                <CreatePermissionDialog show={showCreate} onClose={() => setShowCreate(false)} />
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['permission-update']} asComponent>
                <UpdatePermissionDialog show={showUpdate} onClose={handleCloseUpdate} permissionId={idWillUpdate} />
            </RequireAuth>
        </AdminLayout>
    )
}

export default PermissionsPage